// V3 Artist IDE guardrail — Pillar 7: Distribution / Distributor Release Bridge.
//
// Distributors (DistroKid, TuneCore, CD Baby, Ditto, Amuse, …) are integrated behind explicit
// adapters. Hard rules:
//   • Do NOT assume direct upload for any distributor unless API/partner access is confirmed.
//   • Every adapter declares its capability flags honestly; nothing defaults to "supported".
//   • The only shipped mode today is manual submission packs (see distroKidBridge.ts).
//   • All API-mode adapters are disabled_by_default until legal/partner review clears them.
//
// This module is pure + framework-free: a registry the UI and worker can query to render honest
// distributor capability tables and gate premium distribution workflows.

/** Identifier for a supported (or custom/unknown) music distributor. */
export type DJSEMDistributorId =
  | "distrokid" | "tunecore" | "cdbaby" | "ditto" | "amuse" | "custom";

/** Capability flags every adapter must declare (V3 Pillar 7 verbatim). */
export interface DJSEMDistributionAdapterFlags {
  manual_only: boolean;
  api_supported: boolean;
  partner_required: boolean;
  oauth_required: boolean;
  legal_review_required: boolean;
  disabled_by_default: boolean;
}

/** Honest, declarative description of a distributor's supported workflow and capability flags. */
export interface DJSEMDistributionAdapterDescriptor {
  id: DJSEMDistributorId;
  label: string;
  flags: DJSEMDistributionAdapterFlags;
  /** Which workflow the adapter supports today. `manual_pack` is the only shipped mode. */
  mode: "manual_pack" | "api" | "unavailable";
  /** Honest, user-facing sentence about what works right now. */
  statusNote: string;
  /** Plan tier that unlocks this distributor workflow (maps to the premium ladder). */
  requiresPlan: "creator" | "creator_pro" | "performer" | "enterprise";
}

/**
 * The registry. Descriptors are data, not behavior — real API adapters would be injected
 * server-side and must still match these declared flags before any call is attempted.
 */
export function listDistributionAdapters(): DJSEMDistributionAdapterDescriptor[] {
  const manualFlags: DJSEMDistributionAdapterFlags = {
    manual_only: true, api_supported: false, partner_required: false,
    oauth_required: false, legal_review_required: false, disabled_by_default: false,
  };
  const futureApiFlags: DJSEMDistributionAdapterFlags = {
    manual_only: true, api_supported: false, partner_required: true,
    oauth_required: true, legal_review_required: true, disabled_by_default: true,
  };
  return [
    {
      id: "distrokid", label: "DistroKid",
      flags: manualFlags, mode: "manual_pack",
      statusNote: "Manual submission pack: metadata, credits, lyrics, artwork and checklist exports you upload yourself. No direct API upload — DistroKid has no confirmed public upload API.",
      requiresPlan: "creator_pro",
    },
    {
      id: "tunecore", label: "TuneCore",
      flags: futureApiFlags, mode: "manual_pack",
      statusNote: "Manual pack export works today. Any TuneCore API integration requires partner approval and legal review first — disabled by default.",
      requiresPlan: "creator_pro",
    },
    {
      id: "cdbaby", label: "CD Baby",
      flags: futureApiFlags, mode: "manual_pack",
      statusNote: "Manual pack export works today. API integration is not validated — disabled by default.",
      requiresPlan: "creator_pro",
    },
    {
      id: "ditto", label: "Ditto Music",
      flags: futureApiFlags, mode: "manual_pack",
      statusNote: "Manual pack export works today. API integration is not validated — disabled by default.",
      requiresPlan: "creator_pro",
    },
    {
      id: "amuse", label: "Amuse",
      flags: futureApiFlags, mode: "manual_pack",
      statusNote: "Manual pack export works today. API integration is not validated — disabled by default.",
      requiresPlan: "creator_pro",
    },
  ];
}

/** Look up one adapter; unknown ids resolve to an honest `unavailable` descriptor. */
export function getDistributionAdapter(id: string): DJSEMDistributionAdapterDescriptor {
  const found = listDistributionAdapters().find((a) => a.id === id);
  if (found) return found;
  return {
    id: "custom", label: id,
    flags: { manual_only: true, api_supported: false, partner_required: true, oauth_required: false, legal_review_required: true, disabled_by_default: true },
    mode: "unavailable",
    statusNote: `"${id}" is not a validated distributor integration. Export a generic manual pack instead.`,
    requiresPlan: "creator_pro",
  };
}

/**
 * Guardrail check: is an API-mode submission allowed for this adapter right now?
 * Returns the blocking reasons (empty = allowed). Nothing today returns empty for `api`,
 * which is exactly the point — the answer changes only when a real partner adapter ships.
 */
export function distributorApiBlockers(adapter: DJSEMDistributionAdapterDescriptor): string[] {
  const blockers: string[] = [];
  if (!adapter.flags.api_supported) blockers.push("no confirmed distributor API");
  if (adapter.flags.partner_required) blockers.push("partner approval required");
  if (adapter.flags.oauth_required) blockers.push("OAuth flow not configured");
  if (adapter.flags.legal_review_required) blockers.push("legal review required");
  if (adapter.flags.disabled_by_default) blockers.push("disabled by default until reviewed");
  return blockers;
}
